import React, { useEffect, useState } from "react";
import { Lock, Unlock, Sparkles, Save, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { mediaFileUrl } from "@/lib/mediaUrls";
import { toast } from "sonner";

const TARGET = new Date("2026-06-22T15:00:00").getTime();
const pad = (n) => String(n).padStart(2, "0");

const MediaPreview = ({ mediaId, mediaList }) => {
  if (!mediaId) return null;
  const item = mediaList?.find((m) => m.id === mediaId);
  const isVideo = item?.media_type === "video";
  return isVideo ? (
    <video src={mediaFileUrl(mediaId)} controls className="w-full rounded-xl shadow-2xl" />
  ) : (
    <img src={mediaFileUrl(mediaId)} alt="Surprise" loading="lazy" className="w-full rounded-xl shadow-2xl" />
  );
};

export default function RevealSection({ adminMode = true }) {
  const [content, setContent] = useState(null); // { unlocked, title?, message?, media_id? }
  const [setupData, setSetupData] = useState({ title: "", message: "", media_id: null });
  const [openSetup, setOpenSetup] = useState(false);
  const [preview, setPreview] = useState(false);
  const [allMedia, setAllMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadContent = async () => {
    try {
      const { data } = await api.get("/reveal/content");
      setContent(data);
    } catch (e) { console.error('error in RevealSection.jsx', e); }
  };

  const loadSetup = async () => {
    if (!adminMode) return;
    try {
      const { data } = await api.get("/reveal/setup");
      setSetupData({
        title: data?.title || "",
        message: data?.message || "",
        media_id: data?.media_id || null,
      });
    } catch (e) { console.error('error in RevealSection.jsx', e); }
  };

  const loadMedia = async () => {
    try {
      const { data } = await api.get("/media", { params: { skip: 0, limit: 100 } });
      setAllMedia(data.items || []);
    } catch (e) { console.error('error in RevealSection.jsx', e); }
  };

  useEffect(() => {
    loadContent();
    loadSetup();
    loadMedia();
  }, []);

  const saveSetup = async () => {
    setSaving(true);
    try {
      await api.put("/reveal/setup", setupData);
      toast.success("Surprise enregistrée ♡", { description: "Elle sera dévoilée le 22 juin à 15h00." });
      setOpenSetup(false);
      loadContent();
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const diff = Math.max(0, TARGET - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff / 3_600_000) % 24);
  const minutes = Math.floor((diff / 60_000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  const unlocked = content?.unlocked || preview;

  return (
    <section id="surprise" data-testid="reveal-section" className="px-6 md:px-12 lg:px-24 py-20 lg:py-28 relative">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="font-accent text-3xl text-rose-300">la surprise du 22 juin</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-rose-50 mt-2">
            {unlocked ? "Dévoilée ♡" : "Scellée jusqu'au jour J"}
          </h2>
          <p className="text-rose-200/70 mt-3 max-w-xl mx-auto">
            {unlocked
              ? "Le moment est arrivé."
              : "Un message et un souvenir secrets, qui ne se révèleront qu'au moment voulu."}
          </p>
        </div>

        <div className="romantic-card rounded-2xl p-8 md:p-14 relative overflow-hidden">
          {unlocked ? (
            <div className="reveal">
              <div className="flex items-center gap-3 mb-6">
                <Unlock className="w-6 h-6 text-rose-400" />
                <span className="text-xs uppercase tracking-[0.3em] text-rose-300">
                  {preview && !content?.unlocked ? "Aperçu propriétaire" : "Surprise dévoilée"}
                </span>
              </div>
              {content?.title || setupData.title ? (
                <h3 className="font-serif text-3xl md:text-5xl text-rose-50 mb-6">
                  {content?.unlocked ? content.title : setupData.title}
                </h3>
              ) : null}
              {(content?.media_id || setupData.media_id) && (
                <div className="mb-8">
                  <MediaPreview mediaId={content?.unlocked ? content.media_id : setupData.media_id} mediaList={allMedia} />
                </div>
              )}
              {(content?.message || setupData.message) && (
                <p className="font-accent text-2xl md:text-3xl text-rose-100 leading-loose whitespace-pre-wrap">
                  {content?.unlocked ? content.message : setupData.message}
                </p>
              )}
              {!content?.title && !content?.message && !content?.media_id && !setupData.title && !setupData.message && (
                <p className="text-rose-200/60 italic text-center">
                  Aucun contenu n'a été configuré pour la surprise.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-rose-900/30 border border-rose-700/40 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-rose-400" />
              </div>
              <p className="font-accent text-3xl text-rose-300 mb-2">Scellée avec amour</p>
              <p className="text-rose-100/80 mb-10 max-w-md mx-auto">
                Cette surprise s'ouvrira automatiquement le <span className="text-rose-300 font-medium">22 juin 2026 à 15h00</span>.
              </p>

              <div className="grid grid-cols-4 gap-3 md:gap-4 max-w-xl mx-auto">
                {[
                  { label: "Jours", value: pad(days) },
                  { label: "Heures", value: pad(hours) },
                  { label: "Min", value: pad(minutes) },
                  { label: "Sec", value: pad(seconds) },
                ].map((u) => (
                  <div key={u.label} className="bg-[#0F0407]/80 border border-rose-900/40 rounded-xl py-4">
                    <div className="font-serif text-3xl md:text-4xl text-rose-50 tabular-nums">{u.value}</div>
                    <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-rose-300/60 mt-1">{u.label}</div>
                  </div>
                ))}
              </div>

              <p className="font-accent text-xl text-rose-300/80 mt-10">
                Patience, Amor... ♡
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
          {adminMode && (
            <Dialog open={openSetup} onOpenChange={setOpenSetup}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-reveal-setup"
                variant="outline"
                className="border-rose-700/60 text-rose-100 hover:bg-rose-900/30 rounded-full h-11 px-6"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Configurer la surprise
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Préparer la surprise</DialogTitle>
                <DialogDescription className="text-rose-200/70">
                  Seul toi peux la voir avant le 22 juin 2026 à 15h00 (mode aperçu).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="space-y-2">
                  <Label>Titre (optionnel)</Label>
                  <Input
                    data-testid="reveal-title-input"
                    value={setupData.title}
                    onChange={(e) => setSetupData({ ...setupData, title: e.target.value })}
                    placeholder="Amor..."
                    className="bg-[#0F0407] border-rose-900/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message secret</Label>
                  <Textarea
                    data-testid="reveal-message-input"
                    value={setupData.message}
                    onChange={(e) => setSetupData({ ...setupData, message: e.target.value })}
                    rows={6}
                    placeholder="Écris ici ton message qui sera dévoilé le 22 juin..."
                    className="bg-[#0F0407] border-rose-900/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Photo / vidéo secrète (optionnelle)</Label>
                  <Select
                    value={setupData.media_id || "none"}
                    onValueChange={(v) => setSetupData({ ...setupData, media_id: v === "none" ? null : v })}
                  >
                    <SelectTrigger data-testid="reveal-media-select" className="bg-[#0F0407] border-rose-900/40">
                      <SelectValue placeholder="Choisir un média" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50 max-h-72">
                      <SelectItem value="none">Aucun</SelectItem>
                      {allMedia.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.media_type === "video" ? "🎬 " : "🖼️ "}
                          {m.caption || m.original_filename}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-rose-200/40">Téléverse d'abord la photo/vidéo dans la galerie.</p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="reveal-save-button"
                  onClick={saveSetup}
                  disabled={saving}
                  className="bg-rose-600 hover:bg-rose-500 text-white rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Sauvegarde..." : "Sceller"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}

          {adminMode && !content?.unlocked && (setupData.title || setupData.message || setupData.media_id) && (
            <Button
              data-testid="reveal-preview-toggle"
              onClick={() => setPreview((p) => !p)}
              variant="ghost"
              className="text-rose-200 hover:bg-rose-900/30 rounded-full h-11 px-6"
            >
              {preview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {preview ? "Cacher l'aperçu" : "Aperçu propriétaire"}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
